import { useCallback, useEffect, useState } from 'react';
import { ChevronDown, RefreshCw, ChevronLeft, ChevronRight } from 'lucide-react';
import { Listbox } from '@headlessui/react';
import Card from '../components/Card';
import Button from '../components/Button';
import { realAPI } from '../api/realEndpoints';
import { useToastNotifications } from '../hooks/useToast';
import { formatCurrency } from '../utils/helpers';
import { getUserImageSrc } from '../utils/userImage';
import './ContentManagement.css';

type SubscriptionStatusFilter = 'all' | 'active' | 'inactive';

interface UserSubscriptionPlan {
  planName?: string;
  type?: string;
  price?: number | string;
}

interface SubscriptionUser {
  name?: string;
  email?: string;
  image?: string
}

interface UserSubscription {
  id: number | string;
  status: string;
  paymentGateway?: string;
  amount?: number | string;
  startDate?: string;
  endDate?: string;
  createdAt?: string;
  plans?: UserSubscriptionPlan | null;
  user?: SubscriptionUser | null;
}

interface UserSubscriptionsResponse {
  activeCounts: number;
  counts: number;
  results: UserSubscription[];
  totalPlansPrice: 0;
}

const subscriptionStatusOptions: { label: string; value: SubscriptionStatusFilter; color?: string }[] = [
  { label: 'All Status', value: 'all' },
  { label: 'Active', value: 'active', color: 'text-green-600' },
  { label: 'Inactive', value: 'inactive', color: 'text-red-600' },
];

const formatDateTime = (dateStr?: string) => {
  if (!dateStr) return '-';

  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getSubscriptionStatusColor = (status?: string) => {
  switch ((status || '').toLowerCase()) {
    case 'active':
      return 'bg-green-100 text-green-800';
    case 'inactive':
      return 'bg-red-100 text-red-800';
    case 'expired':
      return 'bg-amber-100 text-amber-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

const getSubscriptionAmount = (subscription: UserSubscription): number | null => {
  const value = subscription.amount ?? subscription.plans?.price;
  if (value === undefined || value === null || value === '') {
    return null;
  }

  const parsed = typeof value === 'string' ? Number(value) : value;
  if (!Number.isFinite(parsed)) {
    return null;
  }

  return Number(parsed);
};

export default function UserSubscriptions() {
  const { error: showError } = useToastNotifications();
  const [subscriptions, setSubscriptions] = useState<UserSubscription[]>([]);
  const [subscriptionStatusFilter, setSubscriptionStatusFilter] =
    useState<SubscriptionStatusFilter>('all');
  const [subscriptionCounts, setSubscriptionCounts] = useState({ total: 0, active: 0, totalPlansPrice: 0 });
  const [page, setPage] = useState(1);
  const [isLoadingSubscriptions, setIsLoadingSubscriptions] = useState(false);
  const ITEMS_PER_PAGE = 10;

  const fetchSubscriptions = useCallback(async () => {
    setIsLoadingSubscriptions(true);
    try {
      const offset = (page - 1) * ITEMS_PER_PAGE;
      const data = (await realAPI.userSubscriptions.getList(
        ITEMS_PER_PAGE,
        offset,
        subscriptionStatusFilter
      )) as UserSubscriptionsResponse;

      if (!data) {
        setSubscriptions([]);
        setSubscriptionCounts({ total: 0, active: 0, totalPlansPrice: 0 });
        return;
      }

      setSubscriptions(Array.isArray(data.results) ? data.results : []);
      setSubscriptionCounts({
        total: Number(data.counts || 0),
        active: Number(data.activeCounts || 0),
        totalPlansPrice: Number(data.totalPlansPrice || 0),
      });
    } catch (err) {
      console.error('Failed to fetch user subscriptions:', err);
      setSubscriptions([]);
      setSubscriptionCounts({ total: 0, active: 0, totalPlansPrice: 0 });
      showError('Error', 'Failed to fetch user subscriptions');
    } finally {
      setIsLoadingSubscriptions(false);
    }
  }, [showError, page, subscriptionStatusFilter]);


  useEffect(() => {
    fetchSubscriptions();
  }, [fetchSubscriptions]);

  const inactiveCounts = Math.max(subscriptionCounts.total - subscriptionCounts.active, 0);

  // No local filtering needed - API handles status filter and pagination
  const totalPages = Math.ceil(subscriptionCounts.total / ITEMS_PER_PAGE);

  return (
    <div className="space-y-4">
      <Card className="p-[10px]">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
          <h1 className="text-2xl font-bold text-gray-900">User Subscriptions</h1>
          <div className="flex flex-wrap gap-3 items-center">
            <div className="relative w-[170px]">
              <Listbox
                value={subscriptionStatusFilter}
                onChange={(value: SubscriptionStatusFilter) => {
                  setSubscriptionStatusFilter(value);
                }}
              >
                <Listbox.Button className="w-full h-10 pl-4 pr-10 text-left bg-white border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all duration-200 hover:border-gray-400 flex items-center overflow-hidden">
                  <span className="block truncate">
                    {subscriptionStatusOptions.find((opt) => opt.value === subscriptionStatusFilter)
                      ?.label || 'Status'}
                  </span>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                </Listbox.Button>
                <Listbox.Options className="absolute z-20 mt-2 w-full bg-white rounded-lg shadow-lg border border-gray-200">
                  {subscriptionStatusOptions.map((option) => (
                    <Listbox.Option
                      key={option.value}
                      value={option.value}
                      className={({ active }) =>
                        `px-4 py-2 cursor-pointer transition-colors ${active ? 'bg-gray-100' : ''}`
                      }
                    >
                      <span className={option.color}>{option.label}</span>
                    </Listbox.Option>
                  ))}
                </Listbox.Options>
              </Listbox>
            </div>

            <Button
              variant="outline"
              className="h-10"
              onClick={() => {
                setSubscriptionStatusFilter('all');
                setPage(1);
                fetchSubscriptions();
              }}
              leftIcon={<RefreshCw className="w-4 h-4" />}
              disabled={isLoadingSubscriptions}
            >
              Refresh
            </Button>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <p className="text-xs text-ink-muted font-semibold uppercase tracking-wider">Total Subscriptions</p>
          <p className="text-2xl font-bold text-ink mt-2">{subscriptionCounts.total}</p>
        </Card>
        <Card>
          <p className="text-xs text-ink-muted font-semibold uppercase tracking-wider">
            Total Earings
          </p>
          <p className="text-2xl font-bold text-ink mt-2">
            £{Number(subscriptionCounts.totalPlansPrice || 0).toFixed(2)}
          </p>
        </Card>
        <Card>
          <p className="text-xs text-ink-muted font-semibold uppercase tracking-wider">Active Subscriptions</p>
          <p className="text-2xl font-bold text-emerald-700 mt-2">{subscriptionCounts.active}</p>
        </Card>
        <Card>
          <p className="text-xs text-ink-muted font-semibold uppercase tracking-wider">Inactive Subscriptions</p>
          <p className="text-2xl font-bold text-red-700 mt-2">{inactiveCounts}</p>
        </Card>
      </div>

      <Card>
        {isLoadingSubscriptions ? (
          <div className="table-container">
            <div className="table-min-w">
              <div className="grid grid-cols-[1.2fr_1.6fr_0.9fr_1fr_0.8fr_1fr_1fr_0.8fr_0.9fr] gap-2 p-4 rounded-t-xl font-semibold text-[15px] border-b border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100">
                <div>User</div>
                <div>Email</div>
                <div>Plan</div>
                <div>Amount</div>
                <div>Type</div>
                <div>Start Date</div>
                <div>End Date</div>
                <div>Status</div>
                <div>Created At</div>
              </div>
              <div className="flex flex-col items-center justify-center py-12">
                <div className="loader"></div>
              </div>
            </div>
          </div>
        ) : subscriptions.length === 0 ? (
          <div className="table-container">
            <div className="table-min-w">
              <div className="grid grid-cols-[1.2fr_1.6fr_0.9fr_1fr_0.8fr_1fr_1fr_0.8fr_0.9fr] gap-2 p-4 rounded-t-xl font-semibold text-[15px] border-b border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100">
                <div>User</div>
                <div>Email</div>
                <div>Plan</div>
                <div>Amount</div>
                <div>Type</div>
                <div>Start Date</div>
                <div>End Date</div>
                <div>Status</div>
                <div>Created At</div>
              </div>
              <div className="text-center py-12 bg-gray-50 rounded-b-xl">
                <img src="/NoRecord.svg" alt="No records found" className="mx-auto mb-4" />
              </div>
            </div>
          </div>
        ) : (
          <div className="table-container">
            <div className="table-min-w">
              <div className="grid grid-cols-[1.2fr_1.6fr_0.9fr_1fr_0.8fr_1fr_1fr_0.8fr_0.9fr] gap-2 p-4 rounded-t-xl font-semibold text-[15px] border-b border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100">
                <div>User</div>
                <div>Email</div>
                <div>Plan</div>
                <div>Amount</div>
                <div>Type</div>
                <div>Start Date</div>
                <div>End Date</div>
                <div>Status</div>
                <div>Created At</div>
              </div>

              <div className="divide-y divide-gray-200 dark:divide-gray-700">
                {subscriptions.map((subscription, index) => {
                  const subscriptionAmount = getSubscriptionAmount(subscription);

                  return (
                    <div
                      key={subscription.id}
                      className="grid grid-cols-[1.2fr_1.6fr_0.9fr_1fr_0.8fr_1fr_1fr_0.8fr_0.9fr] gap-2 p-4 hover:bg-blue-50 transition-colors items-center"
                    >
                      <div className="flex items-center gap-3">
                        {subscription.user?.image ? (
                          <img
                            src={getUserImageSrc(subscription.user?.image, undefined) || "/default-user.png"}
                            alt={subscription.user?.name || "User"}
                            className="w-10 h-10 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center text-2xl font-bold text-gray-700">
                            {subscription.user?.name?.charAt(0)?.toUpperCase() || "-"}
                          </div>
                        )}

                        <div className="text-sm text-gray-800">
                          {subscription.user?.name || "-"}
                        </div>
                      </div>
                      <div
                        className="text-sm text-gray-600 truncate"
                        title={subscription.user?.email}
                      >
                        {subscription.user?.email || '-'}
                      </div>
                      <div className="text-sm text-gray-700">
                        {subscription.plans?.planName || '-'}
                      </div>
                      <div className="text-sm text-gray-700">
                        {subscriptionAmount !== null ? formatCurrency(subscriptionAmount) : '-'}
                      </div>
                      <div className="text-sm text-gray-700">{subscription.plans?.type || '-'}</div>
                      <div className="text-sm text-gray-600 whitespace-nowrap">
                        {formatDateTime(subscription.startDate)}
                      </div>
                      <div className="text-sm text-gray-600 whitespace-nowrap">
                        {formatDateTime(subscription.endDate)}
                      </div>
                      <div className="text-sm">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-md capitalize ${getSubscriptionStatusColor(
                            subscription.status
                          )}`}
                        >
                          {subscription.status || '-'}
                        </span>
                      </div>
                      <div className="text-sm text-gray-600 whitespace-nowrap">
                        {formatDateTime(subscription.createdAt)}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-6 pt-6 border-t border-gray-200 dark:border-gray-600">
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  Showing {(page - 1) * ITEMS_PER_PAGE + 1} to{' '}
                  {Math.min(page * ITEMS_PER_PAGE, subscriptionCounts.total)} of{' '}
                  {subscriptionCounts.total} results
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setPage(page - 1)}
                    disabled={page === 1}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="px-4 py-2 text-sm font-medium text-gray-700">
                    Page {page} of {totalPages}
                  </span>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setPage(page + 1)}
                    disabled={page === totalPages}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </Card>
    </div>
  );
}
